import React, { useRef, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import restaurantSelectors from '@selectors/restaurant';
import { IconPlus } from '@tabler/icons-react';
import { calcBeilagePriceCoefficient, calcCoefficient } from '@utils';
import { Button, Flex, Form, Modal } from 'antd';

import BeilageTemplateForm from './BeilageTemplateForm';
import IngredientModal from './IngredientModal';

const message = defineMessages({
    objectName: 'Beilages',
});

const BeilageTemplateListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const [showIngredientModal, setShowIngredientModal] = useState(false);
    const [form] = Form.useForm();
    const [beilageDataSelected, setBeilageDataSelected] = useState([]);
    const [activeBeilageIndex, setActiveBeilageIndex] = useState(null);
    const [savedSearchParams, setSavedSearchParams] = useState(null);
    const formRef = useRef();

    const { execute: executeGetById, loading: loadingGetById } = useFetch(apiConfig.beilage.getById);

    const {
        data: dataList,
        execute: executeGetIngredient,
        loading: loadingGetIngredient,
    } = useFetch(apiConfig.ingredients.getList);

    const currentRestaurant = useSelector(restaurantSelectors.selectDetailRestaurant);

    const handleCancel = () => {
        onCancelModal();
        setDetail(null);
        form.resetFields();
    };

    const handleIngredientCancel = () => {
        setShowIngredientModal(false);
        navigate({ search: savedSearchParams });
    };

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading: listLoading,
        pagination,
        titleModal,
        executeSave,
        loadingSave,
        openModal,
        onCancelModal,
        onShowModal,
        detail,
        setDetail,
        setIsChangedFormValues,
        onSave,
    } = useListModal({
        apiConfig: apiConfig.beilage,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            const originalOnShowModal = funcs.onShowModal;
            funcs.onShowModal = (isEditing, item) => {
                originalOnShowModal(isEditing, item);
                if (isEditing && item) {
                    form.setFieldsValue({ ...item });
                } else {
                    form.resetFields();
                    form.setFieldsValue({ status: AppConstants.STATUS_ACTIVE, beilages: [] });
                }
            };
        },
    });

    const columns = [
        { title: translate.formatMessage(commonMessage.name), dataIndex: 'name' },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn({ edit: true, delete: true }, { width: '120px' }),
    ];

    const searchFields = [{ key: 'name', placeholder: translate.formatMessage(commonMessage.name) }];

    const handleAddMoreBeilage = (beilageIndex) => {
        setActiveBeilageIndex(beilageIndex);
        const beilages = formRef.current.getFieldValue('beilages') || [];
        setBeilageDataSelected(beilages?.[beilageIndex]?.food_datas || []);
        setSavedSearchParams(location.search);
        setShowIngredientModal(true);
        navigate({ search: '' });
    };

    const handleSelectBeilage = (beilageRaw) => {
        setShowIngredientModal(false);
        setIsChangedFormValues(true);
        const beilage = {
            id: beilageRaw.id,
            name_food: beilageRaw.foodConditionName,
            price: calcCoefficient(beilageRaw.foodConditionPrice, 100),
            plu: beilageRaw.plu,
        };

        const beilages = formRef.current.getFieldValue('beilages') || [];

        if (activeBeilageIndex !== null && beilages[activeBeilageIndex]) {
            if (beilages[activeBeilageIndex].food_datas) {
                beilages[activeBeilageIndex].food_datas.push({ ...beilage });
            } else {
                beilages[activeBeilageIndex].food_datas = [{ ...beilage }];
            }
            formRef.current.setFieldsValue({ beilages });
        }

        setBeilageDataSelected((prev) => [...prev, beilage]);
    };

    const handleSubmit = (values) => {
        const beilagesList = values?.beilages || [];

        const newBeilageItems = beilagesList.map((beilageItem) => ({
            ...beilageItem,
            option: beilageItem.option ? 1 : 0,
        }));

        const preparedData = {
            ...values,
            id: detail?.id,
            restaurantId: detail ? undefined : currentRestaurant.id,
            beilages: calcBeilagePriceCoefficient(newBeilageItems, 0.01),
        };

        onSave(preparedData);
    };

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(message.objectName) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields })}
                actionBar={mixinFuncs.renderActionBar({
                    objectName: translate.formatMessage(commonMessage.beilage),
                })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={listLoading || loadingGetById || loadingSave}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    title={
                        detail
                            ? translate.formatMessage(commonMessage.updateBeilage)
                            : translate.formatMessage(commonMessage.createBeilage)
                    }
                    width={1000}
                    isModalVisible={openModal}
                    form={form}
                    isEditing={!!detail}
                    handleCancel={handleCancel}
                    customStyle={{ padding: '10px' }}
                    noBodyPadding={true}
                >
                    <div style={{ maxHeight: '750px', overflowY: 'auto', padding: '0px 16px 40px 16px' }}>
                        <BeilageTemplateForm
                            formId="beilage-form"
                            handleAddMoreBeilage={handleAddMoreBeilage}
                            formRef={formRef}
                            form={form}
                            isEditing={!!detail}
                            dataDetail={detail}
                            onSubmit={handleSubmit}
                            actions={mixinFuncs.renderActions()}
                            setIsChangedFormValues={setIsChangedFormValues}
                        />
                    </div>
                </BaseModal>
            )}
            {showIngredientModal && (
                <BaseModal
                    title={translate.formatMessage(commonMessage.selectBeilage)}
                    width={1000}
                    isModalVisible={showIngredientModal}
                    handleCancel={handleIngredientCancel}
                    hideSave={true}
                    bodyStyle={{ maxHeight: '90vh', overflowY: 'auto' }}
                    noBodyPadding
                >
                    <IngredientModal
                        loading={loadingGetIngredient}
                        dataList={dataList}
                        currentRestaurant={currentRestaurant}
                        beilageDataSelected={beilageDataSelected}
                        handleSelectBeilage={handleSelectBeilage}
                        getDataList={executeGetIngredient}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default BeilageTemplateListPage;
